import { Button } from "@do/ui/components/button";
import { cn } from "@do/ui/lib/utils";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { PhoneCallIcon, PhoneIcon, XIcon } from "lucide-react";
import { motion, useIsPresent } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { useLayoutEffect, useRef } from "react";
import { COUNTRY_BY_LOCALE } from "../../../constants";
import { useMotionEnabled } from "../../../hooks/use-motion-enabled";
import {
  getRevealProps,
  MESSAGE_REVEAL_OFFSET_Y,
  MESSAGE_REVEAL_TIMING,
} from "../../../lib/motion-presets";

const formatPhoneNumber = (value: string, locale: string) => {
  const displayValue = value.trim().replace(/^tel:/i, "");
  const normalizedValue = displayValue.replace(/^00/, "+");
  const language = locale.split(/[-_]/)[0];
  const phoneNumber = parsePhoneNumberFromString(normalizedValue, COUNTRY_BY_LOCALE[language]);

  if (phoneNumber) {
    return normalizedValue.startsWith("+")
      ? phoneNumber.formatInternational()
      : phoneNumber.formatNational();
  }

  return displayValue;
};

export type CallConfirmationProps = {
  phoneNumber: string;
  onConfirm: () => void;
  onCancel: () => void;
};

export const CallConfirmation = ({ phoneNumber, onConfirm, onCancel }: CallConfirmationProps) => {
  const t = useTranslations("do-chat-common");
  const locale = useLocale();
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber, locale);
  const motionEnabled = useMotionEnabled();

  // Rendered straight inside the list's `AnimatePresence`, so it can read its own presence
  // without a wrapper. A panel on its way out is still in the document and still clickable:
  // here that means confirming a call that is visibly leaving, and placing it. `inert` makes
  // the exit purely visual.
  const isPresent = useIsPresent();
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isPresent) {
      return;
    }

    const node = ref.current;
    const active = document.activeElement;

    if (node && active instanceof HTMLElement && node.contains(active)) {
      active.blur();
    }
  }, [isPresent]);

  return (
    <motion.div
      inert={!isPresent || undefined}
      ref={ref}
      {...getRevealProps({
        motionEnabled,
        timing: MESSAGE_REVEAL_TIMING,
        offsetY: MESSAGE_REVEAL_OFFSET_Y,
        withExit: true,
      })}
      className={cn(
        "relative flex flex-col gap-4",
        "p-(--call-confirmation-padding)",
        "max-w-(--call-confirmation-max-width)",
        "rounded-(--call-confirmation-border-radius)",
        "border-(length:--call-confirmation-border-width)",
        "border-(--call-confirmation-border-color)/(--call-confirmation-border-opacity)",
        "bg-(--call-confirmation-background)/(--call-confirmation-background-opacity)"
      )}
    >
      <Button
        id="call-confirmation-close-button"
        variant="ghost"
        size="icon"
        className="absolute top-(--call-confirmation-padding) right-(--call-confirmation-padding) p-0 size-fit bg-transparent hover:bg-transparent"
        onClick={onCancel}
        aria-label={t("callConfirmation.cancel")}
      >
        <XIcon
          aria-hidden="true"
          className="size-(--call-confirmation-close-button-size) text-(--call-confirmation-close-button-color)/(--call-confirmation-close-button-color-opacity)"
        />
      </Button>

      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-full",
            "bg-(--call-confirmation-call-icon-background)/(--call-confirmation-call-icon-background-opacity)",
            "text-(--call-confirmation-call-icon-color)/(--call-confirmation-call-icon-color-opacity)"
          )}
          aria-hidden="true"
        >
          <PhoneIcon className="size-(--call-confirmation-call-icon-size)" />
        </div>
        <div className="min-w-0 flex flex-col gap-1.5">
          <p
            className={cn(
              "text-(--call-confirmation-title-color)/(--call-confirmation-title-color-opacity)",
              "text-(length:--call-confirmation-title-font-size)",
              "font-(--call-confirmation-title-font-weight)! font-(family-name:--call-confirmation-title-font-family)",
              "leading-[1]"
            )}
          >
            {t("callConfirmation.title")}
          </p>
          <p
            dir="ltr"
            className={cn(
              "mt-0.5 truncate tabular-nums",
              "text-(--call-confirmation-number-color)/(--call-confirmation-number-color-opacity)",
              "text-(length:--call-confirmation-number-font-size)",
              "font-(--call-confirmation-number-font-weight)! font-(family-name:--call-confirmation-number-font-family)",
              "leading-[1]"
            )}
          >
            {formattedPhoneNumber}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          className={cn(
            "bg-(--call-confirmation-cancel-button-background)/(--call-confirmation-cancel-button-background-opacity)",
            "hover:bg-(--call-confirmation-cancel-button-hover-background)/(--call-confirmation-cancel-button-hover-background-opacity)",
            "text-(--call-confirmation-cancel-button-foreground)/(--call-confirmation-cancel-button-foreground-opacity)",
            "hover:text-(--call-confirmation-cancel-button-hover-foreground)/(--call-confirmation-cancel-button-hover-foreground-opacity)",
            "border-(length:--call-confirmation-cancel-button-border-width)",
            "border-(--call-confirmation-cancel-button-border-color)/(--call-confirmation-cancel-button-border-opacity)",
            "hover:border-(--call-confirmation-cancel-button-hover-border-color)/(--call-confirmation-cancel-button-hover-border-opacity)",
            "rounded-(--call-confirmation-cancel-button-border-radius)",
            "h-(--call-confirmation-cancel-button-height)",
            "gap-(--call-confirmation-cancel-button-gap)",
            "text-(length:--call-confirmation-cancel-button-font-size)",
            "font-(--call-confirmation-cancel-button-font-weight)! font-(family-name:--call-confirmation-cancel-button-font-family)",
            "[box-shadow:var(--call-confirmation-cancel-button-box-shadow)]"
          )}
        >
          {t("callConfirmation.cancel")}
        </Button>
        <Button
          size="sm"
          onClick={onConfirm}
          className={cn(
            "group/call-confirmation-call-button",
            "bg-(--call-confirmation-call-button-background)/(--call-confirmation-call-button-background-opacity)",
            "hover:bg-(--call-confirmation-call-button-hover-background)/(--call-confirmation-call-button-hover-background-opacity)",
            "text-(--call-confirmation-call-button-foreground)/(--call-confirmation-call-button-foreground-opacity)",
            "hover:text-(--call-confirmation-call-button-hover-foreground)/(--call-confirmation-call-button-hover-foreground-opacity)",
            "border-(length:--call-confirmation-call-button-border-width)",
            "border-(--call-confirmation-call-button-border-color)/(--call-confirmation-call-button-border-opacity)",
            "hover:border-(--call-confirmation-call-button-hover-border-color)/(--call-confirmation-call-button-hover-border-opacity)",
            "rounded-(--call-confirmation-call-button-border-radius)",
            "h-(--call-confirmation-call-button-height)",
            "gap-(--call-confirmation-call-button-gap)",
            "text-(length:--call-confirmation-call-button-font-size)",
            "font-(--call-confirmation-call-button-font-weight)! font-(family-name:--call-confirmation-call-button-font-family)",
            "[box-shadow:var(--call-confirmation-call-button-box-shadow)]"
          )}
        >
          <PhoneCallIcon
            className={cn(
              "size-(--call-confirmation-call-button-icon-size) text-(--call-confirmation-call-button-icon-color)",
              "group-hover/call-confirmation-call-button:text-(--call-confirmation-call-button-hover-icon-color)"
            )}
            aria-hidden="true"
          />
          {t("callConfirmation.call")}
        </Button>
      </div>
    </motion.div>
  );
};
